import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  ArrowRight,
  Dumbbell,
  LayoutDashboard,
  UserCheck,
  QrCode,
  CalendarClock,
  Users,
  CreditCard,
  BarChart3,
} from 'lucide-react';

const GmsPage = () => {
  const { t } = useTranslation();
  const [previewFailed, setPreviewFailed] = useState(false);

  const features = [
    { icon: UserCheck,     title: t('gmsPage.f1Title'), description: t('gmsPage.f1Desc') },
    { icon: QrCode,        title: t('gmsPage.f2Title'), description: t('gmsPage.f2Desc') },
    { icon: CalendarClock, title: t('gmsPage.f3Title'), description: t('gmsPage.f3Desc') },
    { icon: Users,         title: t('gmsPage.f4Title'), description: t('gmsPage.f4Desc') },
    { icon: CreditCard,    title: t('gmsPage.f5Title'), description: t('gmsPage.f5Desc') },
    { icon: BarChart3,     title: t('gmsPage.f6Title'), description: t('gmsPage.f6Desc') },
  ];

  const highlights = [
    t('gmsPage.h1'),
    t('gmsPage.h2'),
    t('gmsPage.h3'),
    t('gmsPage.h4'),
  ];

  return (
    <>
      <Helmet>
        <title>GMS - Gym Management System | mwbTechs</title>
        <meta
          name="description"
          content="Gym Management System for memberships, check-ins, class schedules, trainer rosters and member billing in one platform."
        />
      </Helmet>

      <div className="pt-20 min-h-screen bg-white">
        {/* Hero Section */}
        <section className="section-sm bg-gradient-to-br from-primary-50 to-blue-50">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-full mb-6">
                  <Dumbbell className="w-4 h-4" />
                  <span className="font-medium">{t('gmsPage.badge')}</span>
                </div>
                <h1 className="text-5xl font-bold text-gray-900 mb-6">
                  {t('gmsPage.heroTitle')}
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  {t('gmsPage.heroSubtitle')}
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/contact" className="btn btn-primary text-lg">
                    {t('gmsPage.requestDemo')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                  <a href="#dashboard" className="btn btn-outline text-lg">
                    {t('gmsPage.seeDashboard')}
                  </a>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">
                    {t('gmsPage.highlightsTitle')}
                  </h2>
                  <ul className="space-y-4">
                    {highlights.map((highlight, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Dashboard Preview */}
        <section id="dashboard" className="section-sm bg-gray-50">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('gmsPage.previewTitle')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('gmsPage.previewSubtitle')}
              </p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-5xl mx-auto"
            >
              <div className="rounded-2xl bg-white border border-gray-200 shadow-2xl overflow-hidden">
                {/* Window chrome */}
                <div className="flex items-center gap-2 px-4 py-3 bg-gray-100 border-b border-gray-200">
                  <span className="w-3 h-3 rounded-full bg-red-400" />
                  <span className="w-3 h-3 rounded-full bg-yellow-400" />
                  <span className="w-3 h-3 rounded-full bg-green-400" />
                  <span className="ml-3 text-sm text-gray-500">{t('gmsPage.previewCaption')}</span>
                </div>

                {/* Screenshot: public/images/gms-dashboard.webp (1897x983) */}
                <div className="aspect-[1897/983] bg-gray-50">
                  {previewFailed ? (
                    <div className="w-full h-full p-4">
                      <div className="w-full h-full flex flex-col items-center justify-center gap-3 border-2 border-dashed border-gray-300 rounded-xl">
                        <LayoutDashboard className="w-12 h-12 text-gray-300" />
                        <p className="text-gray-500 font-medium">{t('gmsPage.previewPlaceholder')}</p>
                        <code className="text-xs text-gray-400">public/images/gms-dashboard.webp</code>
                      </div>
                    </div>
                  ) : (
                    <img
                      src="/images/gms-dashboard.webp"
                      alt={t('gmsPage.previewAlt')}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                      decoding="async"
                      onError={() => setPreviewFailed(true)}
                    />
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="section">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('gmsPage.featuresTitle')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('gmsPage.featuresSubtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-lg transition-shadow"
                  >
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-sm bg-gradient-primary text-white">
          <div className="container-custom text-center">
            <h2 className="text-4xl font-bold mb-4">
              {t('gmsPage.ctaTitle')}
            </h2>
            <p className="text-xl text-white opacity-90 mb-8">
              {t('gmsPage.ctaSubtitle')}
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/contact" className="btn btn-white text-lg">
                {t('gmsPage.requestDemo')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link
                to="/contact"
                className="btn btn-outline text-white border-white hover:bg-white hover:text-primary-600 text-lg"
              >
                {t('gmsPage.talkToSales')}
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default GmsPage;
